import React from "react";
import { useNavigate } from "react-router-dom";
import Layout, { PageHeader } from "@finance/components/Layout";
import { Percent, CaretRight, TreeStructure, Coins, Truck, Package, ClipboardText, Receipt, Airplane } from "@phosphor-icons/react";

const REPORTS = [
  {
    to: "/reports/financials",
    title: "Financial Statements",
    desc: "P&L, Balance Sheet, Trial Balance and Cash Flow — compare months, quarters or years, variance, same period last year, and drill down to every transaction.",
    icon: ClipboardText,
    testid: "report-card-financials",
  },
  {
    to: "/reports/vat-reconciliation",
    title: "VAT Reconciliation",
    desc: "Every transaction entered in a month, matched against the date it was reflected in the filed TRA VAT return.",
    icon: Percent,
    testid: "report-card-vat-reconciliation",
  },
  {
    to: "/reports/forex",
    title: "Forex",
    desc: "Realized settlement gains/losses, period-end unrealized revaluation, and historical catch-up — all reconciled to TZS.",
    icon: Coins,
    testid: "report-card-forex",
  },
  {
    to: "/reports/end-to-end/local",
    title: "End-to-End — Local",
    desc: "Full PO lifecycle for LOCAL transactions — one row per line item, from order through GRV, invoice and payment.",
    icon: TreeStructure,
    testid: "report-card-e2e-local",
  },
  {
    to: "/reports/end-to-end/import",
    title: "End-to-End — Import",
    desc: "Full PO lifecycle for IMPORT transactions — one row per line item, with landed cost and clearing detail.",
    icon: TreeStructure,
    testid: "report-card-e2e-import",
  },
  {
    to: "/reports/fulfillment?tab=pending-signed-delivery-notes",
    title: "Pending Signed Delivery Notes",
    desc: "Delivery Notes issued but still awaiting a signed copy to be attached for the file.",
    icon: Truck,
    testid: "report-card-pending-signed-dn",
  },
  {
    to: "/reports/fulfillment?tab=pending-signed-grv",
    title: "Pending Signed GRV",
    desc: "Goods Received Vouchers issued but still awaiting a signed copy to be attached.",
    icon: Package,
    testid: "report-card-pending-signed-grv",
  },
  {
    to: "/reports/fulfillment?tab=grni",
    title: "GRNI — Goods Received Not Invoiced",
    desc: "Goods received into stock that the supplier has not yet invoiced — the outstanding accrual to clear.",
    icon: ClipboardText,
    testid: "report-card-grni",
  },
  {
    to: "/reports/fulfillment?tab=delivered-not-invoiced",
    title: "Delivered — Client Invoice Pending",
    desc: "Goods delivered to clients on a Delivery Note but not yet billed on a client invoice.",
    icon: Receipt,
    testid: "report-card-delivered-not-invoiced",
  },
  {
    to: "/reports/trips",
    title: "Trip Reports",
    desc: "Business Development trips with every linked expense — trip-level totals, status breakdown, and who submitted each cost.",
    icon: Airplane,
    testid: "report-card-trips",
  },
];

export default function Reports() {
  const navigate = useNavigate();
  return (
    <Layout>
      <PageHeader title="Reports" subtitle="Centralised, printable reports for the finance team" />
      <div className="p-8">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="reports-grid">
          {REPORTS.map((r) => (
            <button
              key={r.to}
              data-testid={r.testid}
              onClick={() => navigate(r.to)}
              className="group flex flex-col items-start rounded-sm border border-gray-200 bg-white p-6 text-left transition-all hover:border-[#1B4383] hover:shadow-md"
            >
              <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-sm bg-[#E6ECF5] text-[#1B4383] transition-colors group-hover:bg-[#1B4383] group-hover:text-white">
                <r.icon size={22} weight="bold" />
              </div>
              <div className="font-display text-base font-bold text-gray-900">{r.title}</div>
              <div className="mt-1 text-sm text-gray-500">{r.desc}</div>
              <div className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-[#1B4383]">
                Open report <CaretRight size={14} weight="bold" className="transition-transform group-hover:translate-x-1" />
              </div>
            </button>
          ))}
        </div>
      </div>
    </Layout>
  );
}
